import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useLabels from '@/hooks/useLabelsParcel';
import useServerPagination from '@/hooks/useServerPagination';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { IResOrderData, IResOrderOK } from '@/types/parcel/orders/orderById.interface';
import {
  DeliveryType,
  IReqOrders,
  IResOrdersOK,
  Platform,
  StatusFilter,
} from '@/types/parcel/orders/orders.interface';
import { IResStatisticsOK } from '@/types/parcel/orders/statistics.interface';
import { labels } from '@/utils/orders';

import { convertDataToPercentage } from '../utils';

interface Shipment {
  loading: boolean;
  error: any;
  data: IResOrdersOK['data']['ordersData'] | [];
  totalPages: number;
}

interface IStatistics {
  data: ChartDataParsedType | null;
  loading: boolean;
  error: any;
}

interface ShipmentDetail {
  loading: boolean;
  error: any;
  data: IResOrderData | null;
}

type ChartDataParsedType = Array<
  Array<{ name: string; value: number | string; realQuantity: number | string; color: string }>
>;

interface Filters extends IReqOrders {
  deliveryType: DeliveryType | undefined;
  statusFilter: StatusFilter | undefined;
  platform: Platform | undefined;
}

export type IFilters = Omit<Filters, 'page' | 'pageSize' | 'search'>;

const useMyShipments = () => {
  const pagination = useServerPagination();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSearchingRef = useRef<boolean>(false);

  const [search, setSearch] = useState<string>('');
  const [searchError, setSearchError] = useState<string>('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [downloadModal, setDownloadModal] = useState<boolean>(false);
  const [trackingIds, setTrackingIds] = useState<string[]>([]);
  const [detailModal, setDetailModal] = useState<boolean>(false);
  const [shipmentDetails, setShipmentDetails] = useState<ShipmentDetail>({
    loading: false,
    error: null,
    data: null,
  });
  const [filters, setFilters] = useState<IFilters>({
    deliveryType: undefined,
    statusFilter: undefined,
    platform: undefined,
    startDate: '',
    endDate: '',
  });
  const [shipments, setShipments] = useState<Shipment>({
    loading: false,
    error: null,
    data: [],
    totalPages: 0,
  });
  const [statistics, setStatistics] = useState<IStatistics>({
    data: null,
    loading: false,
    error: null,
  });
  // Fetchers functions ----------------

  const fetchShipments = useCallback(
    async ({ deliveryType, statusFilter, endDate, startDate, search }: IReqOrders) => {
      setShipments((prev) => ({ ...prev, loading: true }));
      const http = initializeHttpProvider();
      try {
        const response = await http.orders.orders({
          deliveryType,
          statusFilter,
          endDate,
          startDate,
          page: pagination.filters.page,
          pageSize: pagination.filters.page_size,
          search,
        });
        const data = (response as IResOrdersOK).data;
        setShipments({
          loading: false,
          error: null,
          data: data.ordersData,
          totalPages: data.totalPages,
        });
      } catch (error) {
        setShipments({ loading: false, error, data: [], totalPages: 0 });
      }
    },
    [pagination.filters.page, pagination.filters.page_size],
  );

  const fetchStatistics = useCallback(
    async ({ startDate, endDate }: Pick<IFilters, 'startDate' | 'endDate'>) => {
      setStatistics({ ...statistics, loading: true });
      const http = initializeHttpProvider();
      try {
        const response = await http.orders.statistics({
          startDate: startDate ?? undefined,
          endDate: endDate ?? undefined,
        });
        const data = (response as IResStatisticsOK).data;
        const parcedData = convertDataToPercentage(data, labels);
        setStatistics({ data: parcedData, loading: false, error: null });
      } catch (error) {
        setStatistics({ ...statistics, loading: false, error });
      }
    },
    [statistics],
  );

  const fetchShipmentsByTrackingId = useCallback(
    async (trackingId: string) => {
      setShipmentDetails({ ...shipmentDetails, loading: true });
      const http = initializeHttpProvider();
      try {
        const response = await http.orders.orderById({ trackingId });
        const data = (response as IResOrderOK).data;
        setShipmentDetails({
          ...shipmentDetails,
          loading: false,
          data: data,
        });
        setDetailModal(true);
      } catch (error) {
        setShipmentDetails({ ...shipmentDetails, loading: false, error });
      }
    },
    [shipmentDetails],
  );

  // Use effects ----------------------

  const currentFilters = useMemo(() => ({ ...filters, search }), [filters, search]);

  useEffect(() => {
    void fetchShipments({});
    void fetchStatistics({ startDate: filters.startDate, endDate: filters.endDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Solo ejecutar si no estamos en la página inicial y no estamos buscando
    if (
      !isSearchingRef.current &&
      pagination.filters.page !== undefined &&
      pagination.filters.page > 0
    ) {
      void fetchShipments(currentFilters);
    }
    isSearchingRef.current = false; // Reset flag después de cada cambio
  }, [pagination.filters.page, pagination.filters.page_size, fetchShipments, currentFilters]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Filters ----------------------

  const handleOpenDrawer = useCallback(() => {
    setOpenDrawer(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const clearFilters = useCallback(async () => {
    const cleanFilters = {
      deliveryType: undefined,
      statusFilter: undefined,
      platform: undefined,
      startDate: '',
      endDate: '',
    };
    setFilters(cleanFilters);
    setSearch('');
    setSearchError('');
    isSearchingRef.current = true;
    await fetchShipments({
      ...cleanFilters,
    });
    await fetchStatistics({ startDate: filters.startDate, endDate: filters.endDate });
    setOpenDrawer(false);
  }, [fetchShipments, fetchStatistics, filters.endDate, filters.startDate]);

  const applyFilters = useCallback(() => {
    isSearchingRef.current = true;
    void fetchShipments({ ...filters, search });
    if (filters.startDate && filters.endDate) {
      void fetchStatistics({ startDate: filters.startDate, endDate: filters.endDate });
    }
    setOpenDrawer(false);
  }, [filters, search, fetchShipments, fetchStatistics]);

  const fetchOrdersWithFilters = useCallback(async () => {
    void fetchShipments({
      deliveryType: filters.deliveryType,
      statusFilter: filters.statusFilter,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      search: search, // Incluir el texto de búsqueda actual
    });
  }, [filters, fetchShipments, search]);

  const onChangeFilter = useCallback(
    (filterName: keyof IFilters, value: string) => {
      setFilters({ ...filters, [filterName]: value });
    },
    [filters, setFilters],
  );

  const hasActiveFilters = (): boolean => {
    const { deliveryType, statusFilter, platform, startDate, endDate } = filters;
    return (
      !!deliveryType ||
      !!statusFilter ||
      !!platform ||
      !!startDate ||
      !!endDate ||
      search.length >= 5
    );
  };

  // Search Input ----------------------

  const onChgSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      // Siempre actualizamos el estado search para mantener el valor del input
      setSearch(value);

      // Limpiar error cuando el usuario está escribiendo
      if (searchError) {
        setSearchError('');
      }

      // Limpiar timer anterior si existe
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (value.length === 0) {
        isSearchingRef.current = true;
        pagination.onChangeFilter({ page: 0 });
        void fetchShipments({
          deliveryType: filters.deliveryType,
          statusFilter: filters.statusFilter,
          endDate: filters.endDate,
          startDate: filters.startDate,
          search: '',
        });
      } else if (value.length >= 5) {
        // Configurar nuevo timer para debounce
        debounceTimerRef.current = setTimeout(() => {
          isSearchingRef.current = true;
          pagination.onChangeFilter({ page: 0 });
          void fetchShipments({
            deliveryType: filters.deliveryType,
            statusFilter: filters.statusFilter,
            endDate: filters.endDate,
            startDate: filters.startDate,
            search: value,
          });
        }, 500);
      }
    },
    [fetchShipments, searchError, filters, pagination],
  );

  const onSearch = useCallback(() => {
    if (search.length > 0 && search.length < 5) {
      setSearchError('La búsqueda debe tener al menos 5 caracteres');
      return; // No permitir búsqueda si tiene menos de 5 caracteres
    }
    setSearchError('');
    isSearchingRef.current = true;
    void fetchOrdersWithFilters();
  }, [fetchOrdersWithFilters, search]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (search.length > 0 && search.length < 5) {
        setSearchError('La búsqueda debe tener al menos 5 caracteres');
        return; // No permitir búsqueda si tiene menos de 5 caracteres
      }
      setSearchError('');
      onSearch();
    }
  };

  // Select rows

  const selectRows = useCallback((trackings: string[]) => {
    if (trackings.length > 10) {
      return;
    }
    setTrackingIds(trackings);
  }, []);

  const cleanTrackingIds = useCallback(() => {
    setTrackingIds([]);
  }, []);

  // Download modal

  const handleOpenDownloadModal = (trackingId?: string) => {
    setDownloadModal(true);
    if (trackingId) {
      setTrackingIds([trackingId]);
    }
  };

  const handleCloseDownloadModal = () => {
    setDownloadModal(false);
  };

  // Details modal

  const handleCloseDetailModal = () => {
    setDetailModal(false);
    setShipmentDetails({ ...shipmentDetails, data: null });
  };

  // useLabels ------

  const { downloadLabels, loading } = useLabels({
    cleanTrackingIds,
    onCloseModal: handleCloseDownloadModal,
  });

  return {
    shipments,
    pagination,
    filters,
    clearFilters,
    search,
    searchError,
    onChgSearch,
    onSearch,
    handleKeyPress,
    handleOpenDrawer,
    handleCloseDrawer,
    openDrawer,
    statistics,
    downloadLabels,
    loading,
    onChangeFilter,
    applyFilters,
    trackingIds,
    selectRows,
    downloadModal,
    handleOpenDownloadModal,
    handleCloseDownloadModal,
    handleCloseDetailModal,
    shipmentDetails,
    detailModal,
    fetchShipmentsByTrackingId,
    hasActiveFilters,
  };
};

export default useMyShipments;
